import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const DuplicateDocumentModal = ({ isOpen, onClose, document, onConfirm }) => {
  const [duplicating, setDuplicating] = useState(false);

  if (!isOpen || !document) return null;

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Duplication failed:', error);
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900">
            Duplicate Document
          </h2>
          <button
            onClick={onClose}
            disabled={duplicating}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FontAwesomeIcon icon="times" className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-slate-700">
            This will create a complete copy of{' '}
            <strong className="text-slate-900">{document.document_name}</strong>
            {document.section_count > 0 && (
              <span> including all {document.section_count} sections</span>
            )}.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <FontAwesomeIcon
                icon="info-circle"
                className="text-blue-600 mt-0.5 flex-shrink-0"
              />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">The duplicate will include:</p>
                <ul className="list-disc list-inside space-y-1 ml-1">
                  <li>All section content and formatting</li>
                  <li>Section order and structure</li>
                  <li>AI generation metadata</li>
                  {document.total_word_count > 0 && (
                    <li>Total of {document.total_word_count.toLocaleString()} words</li>
                  )}
                </ul>
                <p className="mt-3 text-blue-700">
                  The new document will be named{' '}
                  <strong>"{document.document_name} (Copy)"</strong> and set to
                  <strong> Draft</strong> status.
                </p>
              </div>
            </div>
          </div>

          {document.is_ai_generated && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
              <FontAwesomeIcon
                icon="robot"
                className="text-amber-600 mt-0.5 flex-shrink-0"
              />
              <p className="text-sm text-amber-800">
                This is an AI-generated document. The duplicate will retain all AI generation
                metadata for reference.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={duplicating}
            className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleDuplicate}
            disabled={duplicating}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {duplicating ? (
              <>
                <FontAwesomeIcon icon="spinner" className="fa-spin" />
                <span>Duplicating...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="copy" />
                <span>Duplicate Document</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateDocumentModal;
